import { motion } from 'framer-motion';

export default function TriviaQuestionCard({
  question,
  selectedOption,
  showExplanation,
  onSelectOption,
  onSubmit,
  onNext,
  isLastQuestion,
}) {
  const isCorrect = selectedOption === question?.correctAnswer;

  const optionsArray = question?.options
    ? Object.entries(question.options).map(([key, text]) => ({ key, text }))
    : [];

  const formatKey = (k) => k.toUpperCase();

  return (
    <motion.div
      key={question?.question}
      initial={{ opacity: 0, x: 30 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -30 }}
      transition={{ duration: 0.35 }}
      className="w-full max-w-2xl rounded-2xl bg-white p-6 shadow-lg"
    >
      <h2 className="mb-2 text-2xl font-bold text-gray-800">
        {question?.question}
      </h2>

      {/* Scenario – now in green tones */}
      {question?.scenario && String(question.scenario).trim() && (
        <div className="mb-6 rounded-lg bg-green-50 p-4 text-green-800">
          <p className="text-lg font-medium leading-relaxed">
            {question.scenario}
          </p>
        </div>
      )}

      <div className="space-y-3">
        {optionsArray.map(({ key, text }) => {
          const isSelected = selectedOption === key;
          const isCorrectAnswer = key === question?.correctAnswer.toLowerCase(); // safer comparison

          let buttonStyle = 'border-gray-200 hover:border-gray-300 hover:bg-gray-50 text-gray-800';

          if (showExplanation) {
            // ── After submission ──
            if (isCorrectAnswer) {
              // Correct answer always highlighted green
              buttonStyle = 'border-green-600 bg-green-50 text-green-800 font-semibold shadow-sm';
            } else if (isSelected) {
              // Selected but wrong → red
              buttonStyle = 'border-red-600 bg-red-50 text-red-800 font-medium';
            } else {
              // Neither selected nor correct → muted
              buttonStyle = 'border-gray-200 bg-gray-50 text-gray-600 opacity-75';
            }
          } else {
            // ── Before submission ──
            if (isSelected) {
              buttonStyle = 'border-green-600 bg-green-50 text-green-800 font-medium shadow-sm';
            }
          }

          return (
            <motion.button
              key={key}
              whileHover={{ scale: showExplanation ? 1 : 1.02 }}
              whileTap={{ scale: showExplanation ? 1 : 0.98 }}
              onClick={() => !showExplanation && onSelectOption(key)}
              disabled={showExplanation}
              className={`w-full rounded-lg border p-4 text-left transition-all ${buttonStyle}`}
            >
              <span className="mr-3 font-bold text-gray-700">
                {formatKey(key)}.
              </span>
              {text}
            </motion.button>
          );
        })}
      </div>

      {!showExplanation ? (
        <motion.button
          whileHover={{ scale: 1.03 }}
          whileTap={{ scale: 0.98 }}
          onClick={onSubmit}
          disabled={!selectedOption}
          className={`mt-8 w-full rounded-lg py-3.5 font-medium text-white transition ${
            selectedOption
              ? 'bg-green-600 hover:bg-green-700'
              : 'cursor-not-allowed bg-gray-400'
          }`}
        >
          Submit Answer
        </motion.button>
      ) : (
        <div className="mt-6 rounded-lg border border-gray-200 bg-gray-50 p-5">
          <p className="text-lg font-medium">
            {isCorrect ? (
              <span className="text-green-700">Correct!</span>
            ) : (
              <span className="text-gray-800">
                The correct answer is{' '}
                <strong className="text-green-700">
                  {formatKey(question?.correctAnswer)}
                </strong>
                .
              </span>
            )}
          </p>

          <p className="mt-4 text-gray-700 leading-relaxed">
            {question?.explanation}
          </p>

          {question?.source && (
            <p className="mt-4 text-sm text-gray-500 italic">
              Source: <span className="font-medium">{question.source}</span>
            </p>
          )}

          <motion.button
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.98 }}
            onClick={onNext}
            className="mt-6 w-full rounded-lg bg-green-600 py-3.5 font-medium text-white hover:bg-green-700"
          >
            {isLastQuestion ? 'Finish' : 'Next Question'}
          </motion.button>
        </div>
      )}
    </motion.div>
  );
}